<script setup lang="ts">
  /**
   * Tabs panel — 03-components/12a-tabs-no-borders · OSCloud DS 1.1.134
   *
   * The borderless tab row and the surface it switches, as one unit. The block itself is
   * `display: flow-root` and nothing else, so it does not collapse margins with what follows it
   * and carries no geometry of its own — the page section below keeps its own rhythm gap.
   *
   * `detached` is the design system's second geometry: the row stops being the panel's top edge
   * and becomes a row *above* it — its hairline still closes the row, a `--bf-sp-3` gap follows,
   * and the panel closes itself on all four sides. That is the **register** form, where a tab row
   * above a table filters the table, so the table keeps its own four edges. Always detached once
   * the table has a toolbar or a pager.
   *
   * The joined geometry is the default and no modifier expresses it. Two of its parts are
   * deliberately **not ported**, because no page here uses them yet:
   * - `.tabs-panel__panel`, the padded white surface for content that brings none of its own.
   * - the rule that strips the top edge off a data table sitting directly in the block. Upstream
   *   scopes it `:not(--detached)` so a table's own stacking rules still win; locally that
   *   stacking lives in `BaseTable`'s scoped styles, so the rule would have to be written and
   *   tested against it rather than copied.
   *
   * Add either here — never in a page — when a page needs the joined form.
   */
  withDefaults(defineProps<{ detached?: boolean }>(), { detached: false })
</script>

<template>
  <div class="tabs-panel" :class="{ 'tabs-panel--detached': detached }">
    <slot />
  </div>
</template>

<style scoped lang="scss">
  .tabs-panel {
    display: flow-root;
  }

  /* Geometry only: the modifier touches the gap after the row. It changes no surface, no
     padding, no type, and nothing about the row itself. The direct-child selector is the card's
     contract — the row has to be the block's own child for its hairline to close it.

     `:deep` is load-bearing: the row arrives through the slot, so it carries the CONSUMER's
     scope attribute, not this component's. A plain `.tabs` here compiles to an attribute that
     element never has, and the gap silently collapses to zero. */
  .tabs-panel--detached > :deep(.tabs) {
    margin-bottom: var(--bf-sp-3);
  }
</style>
